import { AwareFloodDataSheet, DeviceGroupList, DeviceTypeList } from 'components/index';
import {
  selectSelectedDeviceId,
  selectUSGSOpen,
} from 'features/integrations/state/selectors/deviceSelectors';
import {
  clearDeviceSettings,
  openAwareSettings,
  openUSGSSettings,
  setSelectedDeviceId,
} from 'features/integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import ConnectedDevicesLoadingState from '../../../components/ConnectedDevicesLoadingState';
import {
  ClearDatasheetParams,
  getPage0Did,
  resolveDeviceFromSearch,
  setDataSheetURLParams,
} from '../utils/urlUtils';
import DeviceListAccordion from './DeviceListAccordion';
import UsgsDataSheet from './usgsDataSheet';

const List = ({ showGroups, showCurrentDevice, widget = false }) => {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isSuccess: devicesLoaded,
  } = useGetConnectedDevicesQuery();
  const { data: deviceTypes = {}, isLoading: deviceTypesLoading } =
    useGetAllAvailableDeviceTypesQuery();
  const usgsOpen = useSelector(selectUSGSOpen);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const [selectedDataDeviceId, setSelectedDataDeviceId] = useState(null);
  const [activeDatasheet, setActiveDatasheet] = useState(null);
  const [groupsVisibleTypes] = useState({
    Flood: true,
    'Manual Road Closure': true,
    'Technology Bridge_Flashing Lights': true,
    'Technology Bridge_Siren/Giant Voice': true,
    'Overwatch VMS': true,
  });

  const currentDevice = useMemo(() => {
    if (selectedDeviceId == null) return null;
    return connectedDevices.find((d) => d.id === selectedDeviceId) || null;
  }, [connectedDevices, selectedDeviceId]);

  // Datasheet Helper
  // Sets local state and (in fullscreen mode) syncs ?uid= + ?did= into the URL.
  const openDatasheet = useCallback(
    (device) => {
      if (!device) return;
      const isAware = DeviceIsSystemType(device, 'Flood');
      const isUSGS = device.system_type === 'USGS';
      if (!isAware && !isUSGS) return;
      setSelectedDataDeviceId(device.id);
      setActiveDatasheet(isUSGS ? 'usgs' : 'Flood');
      if (!widget) {
        setParams((prev) => setDataSheetURLParams(prev, device.id, getPage0Did(device)), {
          replace: true,
        });
      }
    },
    [setParams, widget],
  );

  // Open the AWARE datasheet when external components ask to show the current device
  useEffect(() => {
    if (!showCurrentDevice || !currentDevice) return;
    openDatasheet(currentDevice);
  }, [showCurrentDevice, currentDevice]);

  // Also open datasheet when Redux requests USGS settings
  useEffect(() => {
    if (usgsOpen && selectedDeviceId != null && connectedDevices) {
      const device = connectedDevices.find((d) => d.id === selectedDeviceId) || null;
      openDatasheet(device);
    }
  }, [usgsOpen, selectedDeviceId, connectedDevices]);

  const onOpenDeviceData = useCallback(
    (deviceId) => {
      if (!connectedDevices) return;
      let device = null;
      if (typeof deviceId !== 'undefined' && deviceId !== null) {
        const idNum = typeof deviceId === 'number' ? deviceId : parseInt(deviceId, 10);
        device = connectedDevices.find((d) => d.id === idNum) || null;
      } else if (selectedDeviceId != null) {
        device = connectedDevices.find((d) => d.id === selectedDeviceId) || null;
      }
      openDatasheet(device);
    },
    [connectedDevices, selectedDeviceId, openDatasheet],
  );

  // Clear Bad Params
  const clearUidParams = useCallback(
    () => !widget && setParams(ClearDatasheetParams, { replace: true }),
    [setParams, widget],
  );

  // Clear ?uid= and ?did= on unmount (fullscreen only — widgets don't own URL params).
  useEffect(() => {
    if (widget) return;
    return () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('uid');
      url.searchParams.delete('did');
      window.history.replaceState(null, '', url);
    };
  }, []);

  const openedFromUrlRef = useRef(false);
  useEffect(() => {
    if (widget) return; // widgets don't restore state from URL params
    if (openedFromUrlRef.current) return;
    if (!devicesLoaded) return;
    openedFromUrlRef.current = true;
    const result = resolveDeviceFromSearch(window.location.search, connectedDevices);
    if (result.status === 'invalid') {
      clearUidParams();
      return;
    }
    if (result.status === 'missing') return;
    // status === 'ok' — URL already has the params, just restore local state.
    setSelectedDataDeviceId(result.device.id);
    setActiveDatasheet(result.datasheetType);
    dispatch(setSelectedDeviceId(result.device.id));
  }, [devicesLoaded, connectedDevices, dispatch, clearUidParams]);

  // Close datasheet handler
  const onDataSheetClose = () => {
    setSelectedDataDeviceId(null);
    setActiveDatasheet(null);
    try {
      dispatch(clearDeviceSettings());
    } catch (e) {}
    if (!widget) setParams(ClearDatasheetParams, { replace: true });
  };

  const onAwareSettingsOpen = (e, device) => {
    if (DeviceIsSystemType(device, 'Flood')) {
      e.stopPropagation();
      dispatch(openAwareSettings(device.id));
    }
  };

  const onUSGSDataSheetOpen = (e, device) => {
    if (device?.system_type === 'USGS') {
      e.stopPropagation();
      dispatch(openUSGSSettings(device.id));
    }
  };

  // map selected device id used for datasheets to an actual device object
  const selectedDeviceById = useMemo(
    () => connectedDevices.find((d) => d.id === selectedDataDeviceId) || null,
    [connectedDevices, selectedDataDeviceId],
  );

  if (activeDatasheet === 'Flood' && selectedDeviceById) {
    return (
      <AwareFloodDataSheet
        key={selectedDataDeviceId ?? selectedDeviceId}
        device={selectedDeviceById}
        onClose={onDataSheetClose}
        onSettingsOpen={(event) => onAwareSettingsOpen(event, selectedDeviceById)}
      />
    );
  }

  //Make a new Datasheet for USGS devices.
  if (activeDatasheet === 'usgs' && selectedDeviceById) {
    return (
      <UsgsDataSheet
        key={selectedDataDeviceId ?? selectedDeviceId}
        device={selectedDeviceById}
        onClose={onDataSheetClose}
        onSettingsOpen={(event) => onUSGSDataSheetOpen(event, selectedDeviceById)}
      />
    );
  }

  return (
    <div className="position-relative d-flex flex-column flex-fill h-100 min-h-0">
      <div className="fw-bold fs-4 ps-3 pb-3">{showGroups ? 'Groups' : 'All Devices'}</div>

      <div className="flex-fill overflow-x-hidden overflow-y-auto z-3">
        <div className="d-none d-lg-block">
          <div className="row p-2 align-items-baseline border-bottom">
            <div className="col-4">Name</div>
            <div className="col-2">ID</div>
            <div className="col-6">Status</div>
          </div>

          {connectedDevicesLoading || deviceTypesLoading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <ConnectedDevicesLoadingState message="Loading device list..." />
            </div>
          ) : (
            <>
              {!showGroups &&
                deviceTypes &&
                typeof deviceTypes === 'object' &&
                Object.keys(deviceTypes).map((key) => (
                  <DeviceTypeList
                    key={key}
                    type={deviceTypes[key]}
                    name={key}
                    onOpenDevice={onOpenDeviceData}
                  />
                ))}

              {showGroups && (
                <DeviceGroupList
                  onOpenDevice={onOpenDeviceData}
                  visibleDeviceTypes={groupsVisibleTypes}
                  widget={widget}
                />
              )}
            </>
          )}
        </div>

        {connectedDevicesLoading || deviceTypesLoading ? (
          <div className="d-flex d-lg-none align-items-center justify-content-center py-5">
            <ConnectedDevicesLoadingState message="Loading device list..." />
          </div>
        ) : (
          <DeviceListAccordion
            showGroups={showGroups}
            onOpenDevice={onOpenDeviceData}
            visibleDeviceTypes={groupsVisibleTypes}
            deviceTypes={deviceTypes}
            widget={widget}
          />
        )}
      </div>
    </div>
  );
};

export default List;
